import { Divider, Tab, Tabs } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import { List } from "@react-admin/ra-rbac";
import { Fragment, useCallback } from "react";
import { Button, Datagrid, DatagridProps, Identifier, ListContextProvider, ListProps, TextField, TextInput, useDataProvider, useListContext, useNotify, useRecordContext, useRefresh, useTranslate } from "react-admin";
import { AvatarField } from "../common/components";
import { create, findOne } from "../services/common/common";
import { getFilterValues, useGetTotals } from "../services/userTeam/actions";

interface UserTeamListProps extends ListProps {
    teamId: Identifier
};

interface UserTeamDatagridProps extends DatagridProps {
    teamId: Identifier
};

const addTeamMember = (notify, refresh, dataProvider, teamId, userId) => () => {
    findOne(dataProvider, "team_member", {
        userId,
        teamId
    }).then((result) => {
        if (!result) {
            create(dataProvider, "team_member", {
                userId,
                teamId
            }).then(() => {
                refresh();
                notify("resources.userTeam.add");
            });
        }
    });
};

const removeTeamMember = (notify, refresh, dataProvider, teamId, userId) => () => {
    findOne(dataProvider, "team_member", {
        userId,
        teamId
    }).then((result) => {
        const id = result?.id;
        if (id) {
            dataProvider.delete("team_member", { id }).then(() => {
                refresh();
                notify("resources.userTeam.remove");
            })
        }
    });
};

const Action = ({ tab, teamId }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();
    const record = useRecordContext();
    const buttonProps = tab === "others" ? {
        label: "resources.add",
        className: "activate",
        onClick: addTeamMember(notify, refresh, dataProvider, teamId, record.id)
    } : {
        label: "resources.remove",
        className: "deactivate",
        onClick: removeTeamMember(notify, refresh, dataProvider, teamId, record.id)
    };
    return <Button {...buttonProps} variant="contained">{tab === "others" ? <PersonAddIcon /> : <DeleteIcon />}</Button>
}

const TabbedDatagrid = (propsReceive: UserTeamDatagridProps) => {
    const { teamId, ...props } = propsReceive;
    const translate = useTranslate();
    const listContext = useListContext();
    const { filterValues, setFilters, displayedFilters } = listContext;
    const handleChange = useCallback((event: React.ChangeEvent<{}>, value?: string) => {
        let f = getFilterValues(filterValues, value, teamId);
        setFilters && setFilters(f, displayedFilters);
    }, [filterValues, setFilters, displayedFilters, teamId]);
    const totals = useGetTotals(filterValues, teamId);
    return (
        <Fragment>
            <Tabs variant="fullWidth" value={filterValues.members !== undefined ? "myteam" : "others"} onChange={handleChange}>
                <Tab label={`${translate("resources.userTeam.myteam")} (${totals.myteam})`} value="myteam" />
                <Tab label={`${translate("resources.userTeam.others")} (${totals.others})`} value="others" />
            </Tabs>
            <Divider />
            <ListContextProvider value={{ ...listContext }}>
                <Datagrid {...props}>
                    <AvatarField source="fullname" />
                    <TextField source="email" />
                    <Action tab={filterValues.members !== undefined ? "myteam" : "others"} teamId={teamId} />
                </Datagrid>
            </ListContextProvider>
        </Fragment>
    );
};

export const UserTeamList = (propsReceive: UserTeamListProps) => {
    const { teamId, ...propsOthers } = propsReceive;
    const baseProps = {
        resource: "user",
        basePath: "/user"
    }
    const props = {
        ...propsOthers,
        ...baseProps,
        title: " ",
        hasCreate: false,
        bulkActionButtons: false
    };
    return <List {...props}
        filterDefaultValues={getFilterValues({}, "myteam", teamId)}
        filters={[
            <TextInput label="resources.user.fields.fullname" source="fullname" alwaysOn />
        ]}>
        <TabbedDatagrid {...baseProps} teamId={teamId} />
    </List>;
};